import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import './style.scss';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../../store';
import { setIsModalOpen, setModalContent } from '../../../store/slices/global';
import Headline from '../../../ui/Headline';
import Button from '../../../ui/Button';
import ButtonsGroup from '../../../ui/ButtonsGroup';
import { MODAL_CONTENT } from '../../../app.config.vars/modalContent';
import Input from '../../../ui/Input';
import Loader from '../../../ui/Loader';
import { hasMaxLength, isNotEmpty, validationErrorMessageTranslationKeys, } from '../../../hooks/formValidator/validators';
import { useValidator } from '../../../hooks/formValidator/useValidator';
import { duplicateAdminRally, getAdminRallies } from '../../../api/admin/rallies';
import { setAdminRallies, setTaskWasPasted } from '../../../store/adminSlices/rallies';
import { useSelector } from 'react-redux';
import { selectClipboardTask, setClipboardTask, setPasteTask, } from '../../../store/adminSlices/clipboard';
import { pasteAdminTask, sortTasks } from '../../../api/admin/tasks';
const ConfirmCopy = ({ unit, rallyTitle, rallyId, rallyInternalTitle, sortedTasks, }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useAppDispatch();
    const [internalTitle, setInternalTitle] = useState('');
    const [submitStatus, setSubmitStatus] = useState(null);
    const clipboardTask = useSelector(selectClipboardTask);
    const inputsValidationSchema = {
        internalTitle: [isNotEmpty, hasMaxLength(250)],
    };
    const { formErrors, setFormErrors, validateInputField } = useValidator({
        inputSchema: inputsValidationSchema,
    });
    const duplicateRally = async () => {
        const validationError = validateInputField({
            fieldName: 'internalTitle',
            value: internalTitle,
        });
        if (validationError) {
            return;
        }
        try {
            setSubmitStatus('pending');
            await duplicateAdminRally(rallyId, { internalTitle });
            const rallies = await getAdminRallies();
            dispatch(setAdminRallies(rallies));
            setSubmitStatus('success');
        }
        catch (error) {
            setSubmitStatus('error');
            if (error.response.data.error.code === 'internal_title_exists') {
                setFormErrors((errors) => ({
                    ...errors,
                    internalTitle: [
                        {
                            messageTranslationKey: validationErrorMessageTranslationKeys.invalidInternalTitle,
                        },
                    ],
                }));
            }
            else {
                dispatch(setIsModalOpen(true));
                dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
            }
        }
    };
    const pasteTask = async () => {
        dispatch(setPasteTask(false));
        try {
            setSubmitStatus('pending');
            if (sortedTasks) {
                await sortTasks(rallyId, sortedTasks?.map((task) => task.taskId));
            }
            await pasteAdminTask({ rallyId, taskId: clipboardTask.id });
            dispatch(setTaskWasPasted(true));
            dispatch(setClipboardTask(null));
            setSubmitStatus('success');
        }
        catch (error) {
            setSubmitStatus('error');
            if (error.response.data.error.code === 'internal_title_exists') {
                setFormErrors((errors) => ({
                    ...errors,
                    internalTitle: [
                        {
                            messageTranslationKey: validationErrorMessageTranslationKeys.invalidInternalTitle,
                        },
                    ],
                }));
            }
            else {
                dispatch(setIsModalOpen(true));
                dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
            }
        }
    };
    const closeModal = () => {
        dispatch(setIsModalOpen(false));
        dispatch(setModalContent(null));
        dispatch(setPasteTask(false));
    };
    const handleSubmitSuccess = () => {
        closeModal();
        if (unit === 'rally') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
        else {
            window.scrollTo({ top: window.document.body.scrollHeight, behavior: 'smooth' });
        }
    };
    return (_jsxs("div", { className: 'modal-content confirm-copy', children: [submitStatus !== 'success' && (_jsxs("div", { className: "modal-content-title", children: [_jsx(Headline, { title: t(unit === 'rally' ? 'admin.duplicate_rally_title' : 'admin.paste_task_title'), type: 'div', layout: 1 }), ((unit === 'rally' && rallyTitle) ||
                        (unit === 'task' && clipboardTask?.title[i18n.language])) && (_jsx(Headline, { title: `"${unit === 'rally' ? rallyTitle : clipboardTask?.title[i18n.language]}"`, type: 'div', layout: 2 })), ((unit === 'rally' && rallyInternalTitle) ||
                        (unit === 'task' && clipboardTask?.internalTitle)) && (_jsx(Headline, { title: unit === 'rally' ? rallyInternalTitle : clipboardTask?.internalTitle, type: 'div', layout: 2, color: '#7c818c' })), unit === 'task' && (rallyTitle || rallyInternalTitle) && (_jsxs(_Fragment, { children: [_jsx(Headline, { title: t('label.into'), type: 'div', layout: 2 }), rallyTitle && _jsx(Headline, { title: `"${rallyTitle}"`, type: 'div', layout: 2 }), rallyInternalTitle && (_jsx(Headline, { title: rallyInternalTitle, type: 'div', layout: 2, color: '#7c818c' }))] }))] })), submitStatus === 'pending' ? (_jsx(Loader, {})) : submitStatus === 'success' ? (_jsxs("div", { className: "modal-content-success-message", children: [_jsx(Headline, { title: t(unit === 'rally'
                            ? 'admin.rally_successfully_duplicated'
                            : 'admin.task_successfully_copied'), type: 'h1' }), _jsx("p", { children: t(unit === 'rally' ? 'admin.can_edit_new_rally' : 'admin.can_edit_new_task') })] })) : unit === 'rally' ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "modal-content-description", children: t(unit === 'rally'
                            ? 'admin.enter_rally_internal_title'
                            : 'admin.enter_task_internal_title') }), _jsx("div", { className: "modal-content-form", children: _jsx(Input, { id: 'internal-title', value: internalTitle, name: "internalTitle", label: t('label.internal_title'), placeholder: t('placeholder.internal_title'), onChange: (event) => {
                                setInternalTitle(event.currentTarget.value);
                                validateInputField({
                                    fieldName: 'internalTitle',
                                    value: event.currentTarget.value,
                                });
                            }, error: !!formErrors.internalTitle?.length, errorMessage: !!formErrors.internalTitle?.length
                                ? t(formErrors.internalTitle[0].messageTranslationKey, formErrors.internalTitle[0].variableToUseInMessage)
                                : undefined }) })] })) : null, _jsx("div", { className: "modal-content-button", children: _jsx(ButtonsGroup, { justify: 'center', children: submitStatus === 'success' ? (_jsx(ButtonsGroup, { children: _jsx(Button, { variant: 'outlined', label: t('button.ok'), onClick: handleSubmitSuccess }) })) : (_jsxs(_Fragment, { children: [_jsx(Button, { label: t('button.cancel'), variant: 'outlined', onClick: closeModal, disabled: submitStatus === 'pending' }), _jsx(Button, { label: t(unit === 'rally' ? 'button.duplicate_rally' : 'button.paste_task'), onClick: unit === 'rally' ? duplicateRally : pasteTask, disabled: submitStatus === 'pending' })] })) }) })] }));
};
export default ConfirmCopy;
